/**
 * external-sharing Phase 1 Session 1.2 — schemas for the deal-link sharing,
 * fork-back, version-graph, version-diff, sandbox-recompute, and public-deal
 * surfaces.
 *
 * Wire format only — does not own DB persistence (that lives in the
 * `shared_links` table created by migration `20260602000000_…`). All schemas
 * are camelCase wire shapes; the supabase query module in Session 1.3 maps
 * snake_case rows → these shapes.
 */
import { z } from 'zod';
// ============================================================
// Viewer kind discriminator (computed by getPublicDeal)
// ============================================================
export const viewerKindSchema = z.enum([
    'external-anon', // unauth OR auth-but-not-recipient
    'external-authed', // approved Arcanum user, not owner, not recipient
    'internal-recipient', // share's recipient_user_id
    'owner', // share's owner_user_id (only via ?preview=true; Phase 6 owner-redirect short-circuits the normal flow)
]);
// ============================================================
// Recipient kind (set at createShare time)
// ============================================================
export const recipientKindSchema = z.enum(['internal', 'external']);
// ============================================================
// Share status
// ============================================================
export const shareStatusSchema = z.enum(['pending', 'active', 'revoked']);
// ============================================================
// Core response shape (mirrors shared_links + joined fields)
// ============================================================
export const sharedLinkResponseSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    conversationTitle: z.string(),
    ownerUserId: z.string().uuid(),
    ownerName: z.string(),
    recipientEmail: z.string().email(),
    recipientUserId: z.string().uuid().nullable(),
    recipientName: z.string().nullable(),
    recipientKind: recipientKindSchema,
    status: shareStatusSchema,
    message: z.string().nullable(),
    parentShareId: z.string().uuid().nullable(),
    viewCount: z.number().int().min(0),
    firstViewedAt: z.string().datetime().nullable(),
    lastViewedAt: z.string().datetime().nullable(),
    recipientFirstViewedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    revokedAt: z.string().datetime().nullable(),
});
// ============================================================
// createShare body (public POST /api/shares + internal shareForkBack call)
// ============================================================
export const createShareBodySchema = z.object({
    conversationId: z.string().uuid(),
    recipientEmail: z.string().email(),
    message: z.string().max(500).optional(),
    // parentShareId is INTERNAL ONLY — populated by shareForkBack via service-internal call.
    // The public POST /api/shares Zod schema (apps/api side) strips this field.
    parentShareId: z.string().uuid().optional(),
});
// API-layer schema: same as createShareBodySchema but rejects parentShareId.
export const createSharePublicBodySchema = createShareBodySchema.omit({ parentShareId: true });
// ============================================================
// PATCH /api/shares/:shareId — only revoke is supported
// (RESOLVES /gap-analysis IMPORTANT #8)
// ============================================================
export const revokeShareBodySchema = z.object({
    status: z.literal('revoked'),
});
// ============================================================
// GET /api/shares?role=sent|received query params
// (RESOLVES /gap-analysis IMPORTANT #9)
// ============================================================
export const listSharesQuerySchema = z.object({
    role: z.enum(['sent', 'received']),
});
// ============================================================
// shareForkBack body
// ============================================================
export const shareForkBackBodySchema = z.object({
    conversationId: z.string().uuid(),
    parentShareId: z.string().uuid(),
    message: z.string().max(500).optional(),
});
// ============================================================
// Version graph
// ============================================================
export const versionGraphChildShareSchema = z.object({
    shareId: z.string().uuid(),
    recipientName: z.string(),
    createdAt: z.string().datetime(),
    status: shareStatusSchema,
});
export const versionGraphNodeSchema = z.object({
    conversationId: z.string().uuid(),
    conversationTitle: z.string(),
    ownerUserId: z.string().uuid(),
    ownerName: z.string(),
    createdAt: z.string().datetime(),
    isOriginal: z.boolean(),
    childShares: z.array(versionGraphChildShareSchema),
    forkedFromShareId: z.string().uuid().nullable(),
});
export const versionGraphResponseSchema = z.object({
    rootConversationId: z.string().uuid(),
    nodes: z.array(versionGraphNodeSchema),
});
// ============================================================
// Diff query (parses serialized "<kind>:<uuid>" sides)
// ============================================================
const diffSideKindSchema = z.enum(['conversation', 'frozen_state']);
export const diffQuerySchema = z
    .object({
    from: z.string(), // serialized as `<kind>:<uuid>` e.g. "conversation:abc-123"
    to: z.string(),
})
    .transform((args, ctx) => {
    const parseSide = (side, s) => {
        const idx = s.indexOf(':');
        if (idx < 0) {
            ctx.addIssue({ code: 'custom', message: `Missing ':' separator in ${side}`, path: [side] });
            return z.NEVER;
        }
        const kindRaw = s.slice(0, idx);
        const id = s.slice(idx + 1);
        const kind = diffSideKindSchema.safeParse(kindRaw);
        if (!kind.success) {
            ctx.addIssue({ code: 'custom', message: `Invalid diff side kind: ${kindRaw}`, path: [side] });
            return z.NEVER;
        }
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
            ctx.addIssue({ code: 'custom', message: `Invalid UUID in ${side}`, path: [side] });
            return z.NEVER;
        }
        return { kind: kind.data, id };
    };
    return { from: parseSide('from', args.from), to: parseSide('to', args.to) };
});
// ============================================================
// Diff
// ============================================================
export const diffSideSchema = z.object({
    kind: diffSideKindSchema,
    id: z.string().uuid(),
});
export const diffInputChangeSchema = z.object({
    name: z.string(),
    /**
     * Optional category sourced from `schema.inputs[i].category`. Drives the
     * client's section grouping (Property / Financing / etc.) so the model
     * author's intent is honored without any client-side categorization
     * heuristic. Absent → client buckets into "Other".
     */
    category: z.string().optional(),
    /** Optional `schema.inputs[i].displayName` for nicer row labels. */
    displayName: z.string().optional(),
    oldValue: z.unknown(),
    newValue: z.unknown(),
    oldSource: z.string().optional(),
    newSource: z.string().optional(),
});
export const diffMetricChangeSchema = z.object({
    name: z.string(),
    /**
     * Optional human-readable label sourced from `schema.outputs[i].displayName`.
     * Model-agnostic: the server projects it from the loaded schema at diff
     * time so the client never hardcodes a metric label map. Absent for
     * back-compat with older API responses.
     */
    displayName: z.string().optional(),
    oldValue: z.number(),
    newValue: z.number(),
    deltaPct: z.number(),
});
const diffInputAddRemoveEntrySchema = z.object({
    name: z.string(),
    /** Optional `schema.inputs[i].category` — same as on `diffInputChangeSchema`. */
    category: z.string().optional(),
    /** Optional `schema.inputs[i].displayName`. */
    displayName: z.string().optional(),
    value: z.unknown(),
    source: z.string().optional(),
});
export const versionDiffResponseSchema = z.object({
    fromConversationId: z.string().uuid().optional(),
    fromFrozenStateShareId: z.string().uuid().optional(),
    toConversationId: z.string().uuid().optional(),
    toFrozenStateShareId: z.string().uuid().optional(),
    inputs: z.object({
        added: z.array(diffInputAddRemoveEntrySchema),
        removed: z.array(diffInputAddRemoveEntrySchema),
        changed: z.array(diffInputChangeSchema),
    }),
    /**
     * Schema-driven render order for input section headers (categories).
     * Server collects categories in the order they first appear in
     * `schema.inputs[]`, so the model author's authoring order is what the
     * client renders. Absent → client falls back to alphabetical order.
     */
    categoryOrder: z.array(z.string()).optional(),
    metrics: z.array(diffMetricChangeSchema).optional(),
});
// ============================================================
// Sandbox-recompute (server-side HyperFormula path; GPL3 — never client)
// ============================================================
export const sandboxRecomputeRequestSchema = z.object({
    overrides: z.record(z.string(), z.unknown()),
    recomputeCells: z.array(z.string()).optional(),
});
export const sandboxErrorCodeSchema = z.enum([
    'CIRCULAR_REF', 'DIV_ZERO', 'VALUE', 'NA', 'NUM', 'NAME', 'REF', 'UNKNOWN',
]);
export const sandboxErrorSchema = z.object({
    cellAddress: z.string(),
    code: sandboxErrorCodeSchema,
    message: z.string(),
    raw: z.string().optional(),
});
export const sandboxRecomputeResponseSchema = z.object({
    values: z.record(z.string(), z.unknown()),
    errors: z.array(sandboxErrorSchema),
});
// ============================================================
// Suggestion chips — LLM-generated NarrativeBar starter questions
// (deal-signal-overhaul Phase 3 Session 1, 2026-05-11)
//
// Persisted once at createShare time onto shared_links.suggestion_chips
// (added by migration 20260605000000). Frozen for all viewers of the link.
// `chips.length === 3` always — the generator must produce exactly 3 or
// the column is NULL'd and the renderer falls back to hardcoded chips.
// ============================================================
export const suggestionChipsSchema = z.object({
    chips: z.array(z.string().min(1).max(200)).length(3),
});
// ============================================================
// PublicDealResponse (extends Phase 6's shape with viewerKind + frozen_state pointer)
// + suggestionChips (deal-signal-overhaul Phase 3 Session 1, 2026-05-11) — populated
//   at createShare time; null when LLM generation failed at share creation.
// ============================================================
export const publicDealResponseSchema = z.object({
    shareId: z.string().uuid(),
    conversationId: z.string().uuid(),
    title: z.string(),
    modelId: z.string(),
    modelVersion: z.string(),
    signalVariant: z.enum(['operating-asset', 'new-development']).nullable(),
    viewerKind: viewerKindSchema,
    ownerName: z.string(),
    message: z.string().nullable(),
    createdAt: z.string().datetime(),
    // frozen_state IS the source of truth — never the live conversation.
    // Typed as unknown at this layer; consumers narrow via
    // conversationAgentStateSchema from agent.types.ts when required.
    frozenState: z.unknown(),
    aerialLatLng: z
        .object({
        lat: z.number(),
        lng: z.number(),
    })
        .nullable()
        .optional(),
    // Required-but-nullable (Phase 3+ always writes the column, even if null).
    // Phase 5 NarrativeBar reads this; falls back to hardcoded 3-chip default
    // when null (pre-Phase-3 shares + LLM-generation failures).
    suggestionChips: suggestionChipsSchema.nullable(),
});
// ============================================================
// Public deal chat — wire shapes for Phase 3's 3 new endpoints
// (deal-signal-overhaul Phase 3 Session 1, 2026-05-11)
//
// Persistence lives in the `share_conversations` table (migration
// 20260605000000_share_conversations_and_telemetry_uncap.sql). Wire shapes
// strip `viewer_session_id` from every turn — server-side privacy contract.
// ============================================================
// One turn projection — the on-the-wire shape. `viewer_session_id` is
// INTENTIONALLY OMITTED here — `SharingService.getPublicConversation`
// projects it away before returning. Streaming-only fields (`thinking`,
// `plan`, `tool_call_states`, `tool_results`, `tool_errors`, `summary`)
// use `z.unknown()` at the wire layer to avoid forcing ts-jest to load
// shared-types/dist ESM at API test time (same lesson as `toolErrorSchema`
// — see apps/api/CLAUDE.md "DO NOT import toolErrorSchema" rule). The
// upstream Zod parse runs at write time inside `agents/streaming.ts`.
export const shareConversationTurnSchema = z.object({
    id: z.string().uuid(),
    turn_index: z.number().int().nonnegative(),
    role: z.enum(['human', 'agent']),
    content: z.string(),
    thinking: z.unknown().nullable().optional(),
    plan: z.unknown().nullable().optional(),
    tool_call_states: z.unknown().nullable().optional(),
    tool_results: z.unknown().nullable().optional(),
    tool_errors: z.unknown().nullable().optional(),
    summary: z.unknown().nullable().optional(),
    created_at: z.string().datetime(),
});
// GET /api/shared/:shareId/conversation response.
export const publicConversationResponseSchema = z.object({
    turns: z.array(shareConversationTurnSchema),
    gateActive: z.boolean(),
    questionCount: z.number().int().min(0).max(3),
});
// GET /api/shared/:shareId/questions response.
export const publicQuestionsResponseSchema = z.object({
    count: z.number().int().min(0).max(3),
    gateActive: z.boolean(),
});
// POST /api/shared/:shareId/conversation/messages request body.
// `overrides` mirrors the SandboxRecomputeRequest shape — `{namedRange: value}`.
export const publicChatRequestSchema = z.object({
    message: z.string().min(1).max(2000),
    overrides: z.record(z.string(), z.unknown()).optional(),
});
